<template>
	<tab_header icon="fa-solid fa-gear" title="Instalação" />

	<div class="row align-items-end">
		<div class="form-group col-12 col-md-6 col-lg-4 align-items-center">
			<label for="agent_installation_code" class="fw-bold"
				>Código de instalação do Agente</label
			>
			<div class="position-relative">
				<input
					:value="agent_installation_code"
					readonly
					type="text"
					class="form-control form-control-solid fw-bolder" />
				<div
					@click="copyAgentCodeToClipboard()"
					data-bs-toggle="tooltip"
					data-bs-placement="top"
					data-bs-title="Copiar"
					class="position-absolute translate-middle-y top-50 end-0 me-3"
					role="button">
					<i class="fa-solid fa-copy fa-xl"></i>
				</div>
			</div>
		</div>
		<div class="col-12 col-md-6 col-lg-4 mt-5 mt-md-0">
			<loading_button
				:loading="loading"
				:action="generateInstallationCode"
				>Gerar novo código
			</loading_button>
		</div>

		<div id="alert" class="mt-5" v-if="generate_code_successfully !== null">
			<div class="alert alert-success" v-if="generate_code_successfully">
				<p class="m-0 fw-bold">{{ response_generate_code.message }}</p>
			</div>

			<div class="alert alert-danger" v-else>
				<p class="m-0 fw-bold">{{ response_generate_code.message }}</p>
			</div>
		</div>
	</div>

	<div class="row mt-10">
		<div class="d-flex justify-content-between align-items-center mb-7">
			<div class="d-flex align-items-center">
				<i class="fa-solid fa-sync fa-xl ms-1 me-3"></i>
				<div>
					<h3 class="card-title m-0">Resetar Instalação do Agente</h3>
					<small class="text-muted d-block">
						Reinicia os códigos de instalação do Agente para uma nova configuração.
					</small>
				</div>
			</div>
		</div>
		<div class="row">
			<div class="col-12 col-md-6">
				<div class="d-grid gap-2">
					<loading_button
						color="danger"
						:loading="reset_agent_loading"
						:action="resetInstallationCode">
						Resetar Instalação
					</loading_button>
				</div>
			</div>

			<div id="alert" class="mt-5" v-if="reset_agent_successfully !== null">
				<div class="alert alert-success" v-if="reset_agent_successfully">
					<p class="m-0 fw-bold">{{ response_reset_agent.message }}</p>
				</div>
				<div class="alert alert-danger" v-else>
					<p class="m-0 fw-bold">{{ response_reset_agent.message }}</p>
				</div>
			</div>

		</div>
	</div>

	<div class="row mt-10">
		<div class="d-flex justify-content-between align-items-center mb-7">
			<div class="d-flex align-items-center">
				<i class="fa-solid fa-download fa-xl ms-1 me-3"></i>
				<h3 class="card-title m-0">Downloads</h3>
			</div>
		</div>
		<div class="row">
			<div class="col-12 col-md-6">
				<div class="d-grid gap-2">
					<loading_button
						:loading="download_loading"
						:action="downloadAgentInstaller">
						Download do instalador
					</loading_button>
				</div>
				<div
					class="alert alert-danger mt-5"
					v-if="download_request_failed">
					<p class="m-0 fw-bold">
						{{ download_request_error_message }}
					</p>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { Ref, inject, ref } from 'vue';
	import tab_header from './tab_header.vue';
	import loading_button from '../Widgets/loading_button.vue';
	import axios from 'axios';
	import Swal from "sweetalert2";

	const props = defineProps({
		generate_installation_code_url: { type: String, required: true },
		reset_agent_installation_url: { type: String, required: true },
		agent_installation_code: { type: String, required: false },
		computer_id: { type: Number, required: true },
	});

	const agent_installation_code = ref(props.agent_installation_code);

	const generate_code_successfully: Ref<null | boolean> = ref(null);
	const reset_agent_successfully: Ref<null | boolean> = ref(null);

	const loading = ref(false);

	interface Response {
		message: string;
		code?: string;
	}

	let response_generate_code: Response;
	let response_reset_agent: Response;

	const agent_installer_download_url = inject(
		'agent_installer_download_url'
	) as string;

	const download_loading = ref(false);
	const reset_agent_loading = ref(false);

	const download_request_failed: Ref<null | boolean> = ref(null);

	let download_request_error_message;

	function copyAgentCodeToClipboard() {
		window.navigator.clipboard.writeText(agent_installation_code.value);
	}

	async function generateInstallationCode() {
		loading.value = true;

		generate_code_successfully.value = null;

		try {
			const response = await axios.post(
				props.generate_installation_code_url,
				{ computer_id: props.computer_id }
			);
			response_generate_code = response.data;
			generate_code_successfully.value = true;
			agent_installation_code.value =
				response_generate_code.code as string;
		} catch (error) {
			response_generate_code = error.response.data;
			generate_code_successfully.value = false;
		} finally {
			loading.value = false;
		}
	}

	async function resetInstallationCode() {
		const confirmReset = await Swal.fire({
			title: "Tem certeza?",
			text: "Essa ação irá resetar a instalação do agente!",
			icon: "warning",
			showCancelButton: true,
			confirmButtonText: "Sim, resetar",
			confirmButtonColor: "#d33",
			cancelButtonText: "Cancelar",
			cancelButtonColor: "#3085d6",
		});

		if (!confirmReset.isConfirmed) return;

		reset_agent_loading.value = true;
		reset_agent_successfully.value = null;

		try {
			const response = await axios.post(
				props.reset_agent_installation_url,
				{ computer_id: props.computer_id }
			);
			response_reset_agent = response.data;
			reset_agent_successfully.value = true;
			agent_installation_code.value = '' as string;
		} catch (error) {
			response_reset_agent = error.response.data;
			reset_agent_successfully.value = false;
		} finally {
			reset_agent_loading.value = false;
		}
	}

	async function downloadAgentInstaller() {
		download_loading.value = true;
		download_request_failed.value = null;

		try {
			const response = await axios.get(agent_installer_download_url);
			const url = response.data.url;
			const link = document.createElement('a');
			link.href = url;
			link.click();
		} catch (error) {
			download_request_failed.value = true;
			download_request_error_message = error.response.data.message;
		} finally {
			download_loading.value = false;
		}
	}
</script>
